import React, { useEffect } from 'react';
import '../TemplateManagement.css';

/**
 * VariablesSection — WhatsApp-style variable system
 * Two types only:
 *   - 'name'   → named variables like {{member_name}}, {{company}}
 *   - 'number' → numbered variables like {{1}}, {{2}}, {{3}}
 * Auto-detects variables from subject, greeting, and body content.
 */
const VariablesSection = ({ formData, onChange }) => {
  const variableType = formData.variableType || 'name';

  /* ── Auto-detect variables from template content ── */
  useEffect(() => {
    const text = (formData.bodyContent || '') + ' ' + (formData.bodyGreeting || '') + ' ' + (formData.headerTitle || '');
    const existingKeys = new Set((formData.variables || []).map((v) => v.key));
    const newVars = [...(formData.variables || [])];
    let changed = false;

    if (variableType === 'number') {
      const numPattern = /\{\{\s*(\d+)\s*\}\}/g;
      let m;
      while ((m = numPattern.exec(text)) !== null) {
        if (!existingKeys.has(m[1])) {
          newVars.push({ key: m[1], type: 'number' });
          existingKeys.add(m[1]);
          changed = true;
        }
      }
    } else {
      const namePattern = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
      let m;
      while ((m = namePattern.exec(text)) !== null) {
        if (!existingKeys.has(m[1])) {
          newVars.push({ key: m[1], type: 'name' });
          existingKeys.add(m[1]);
          changed = true;
        }
      }
    }

    if (changed) onChange({ variables: newVars });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bodyContent, formData.bodyGreeting, formData.headerTitle, variableType]);

  /* ── Add variable ── */
  const addVariable = () => {
    if (variableType === 'number') {
      const maxNum = (formData.variables || []).reduce((mx, v) => {
        const n = parseInt(v.key, 10);
        return !isNaN(n) && n > mx ? n : mx;
      }, 0);
      onChange({
        variables: [...(formData.variables || []), { key: String(maxNum + 1), type: 'number' }]
      });
    } else {
      onChange({
        variables: [...(formData.variables || []), { key: '', type: 'name' }]
      });
    }
  };

  /* ── Remove variable ── */
  const removeVariable = (index) => {
    onChange({ variables: (formData.variables || []).filter((_, i) => i !== index) });
  };

  /* ── Update variable key (name type only) ── */
  const updateVariableKey = (index, value) => {
    const newVars = [...(formData.variables || [])];
    newVars[index] = { ...newVars[index], key: value.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '') };
    onChange({ variables: newVars });
  };

  return (
    <div className="emtpl-section">
      <div className="emtpl-vars-list">
        {(formData.variables || []).map((variable, index) => (
          <div key={index} className="emtpl-var-item">
            <div className="emtpl-var-fields">
              <span className="em-badge em-badge-xs">
                {variableType === 'number' ? '#' : 'Aa'}
              </span>

              {variableType === 'number' ? (
                <span className="em-font-mono" style={{ fontSize: '0.82rem', color: 'var(--em-text-muted)', minWidth: 60 }}>
                  {`{{${variable.key}}}`}
                </span>
              ) : (
                <input
                  type="text"
                  className="em-input em-font-mono"
                  value={variable.key}
                  onChange={(e) => updateVariableKey(index, e.target.value)}
                  placeholder="e.g., member_name"
                  style={{ flex: 1, minWidth: 0 }}
                />
              )}

              {variable.key && (
                <span className="emtpl-var-usage">{`{{${variable.key}}}`}</span>
              )}
            </div>

            <button
              type="button"
              className="emtpl-var-remove"
              onClick={() => removeVariable(index)}
              title="Remove variable"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <button type="button" className="emtpl-var-add" onClick={addVariable}>
        + Add Variable
      </button>

      {(formData.variables || []).length === 0 && (
        <p className="em-text-xs em-text-muted" style={{ marginTop: 8 }}>
          No variables yet. Add variables or type {'{{variable_name}}'} in body to auto-detect.
        </p>
      )}
    </div>
  );
};

export default VariablesSection;
